import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

interface Promotion {
  id: number;
  store_id: number;
  store_name: string;
  store_city: string;
  store_state: string;
  title: string;
  message: string;
  image_url: string | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_current: boolean;
  status: 'active' | 'scheduled' | 'expired' | 'inactive';
  created_at: string;
  created_by_name: string | null;
}

interface PromotionStats {
  total_promotions: number;
  active: number;
  scheduled: number;
  expired: number;
  inactive: number;
  stores_with_promotions: number;
}

interface Store {
  id: number;
  name: string;
}

const BridgeClubPromotions: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [stats, setStats] = useState<PromotionStats | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedStore, selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedStore) params.store_id = selectedStore;
      if (selectedStatus) params.status = selectedStatus;

      const [promotionsRes, statsRes] = await Promise.all([
        api.get('/admin/promotions/list_promotions/', { params }),
        api.get('/admin/promotions/stats/'),
      ]);

      setPromotions(promotionsRes.data.promotions || []);
      setStores(promotionsRes.data.stores || []);
      setStats(statsRes.data);
      setError(null);
    } catch (err: any) {
      setError('Failed to load promotions');
      console.error('Error loading promotions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      await api.post(`/admin/promotions/${id}/toggle_active/`);
      await loadData();
    } catch (err: any) {
      alert('Failed to toggle promotion status');
      console.error('Error toggling promotion:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, React.CSSProperties> = {
      active: { background: '#DEF7EC', color: '#03543F', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 },
      scheduled: { background: '#E1EFFE', color: '#1E40AF', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 },
      expired: { background: '#F3F4F6', color: '#6B7280', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 },
      inactive: { background: '#FEE2E2', color: '#991B1B', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 },
    };
    return <span style={styles[status] || styles.inactive}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const filteredPromotions = promotions.filter(promo => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      promo.title.toLowerCase().includes(query) ||
      promo.store_name.toLowerCase().includes(query) ||
      promo.message.toLowerCase().includes(query)
    );
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <Link to="/admin" style={{ color: '#6B7280', textDecoration: 'none' }}>Admin</Link>
          <span style={{ color: '#9CA3AF' }}>/</span>
          <span style={{ color: '#111827', fontWeight: 500 }}>Store Promotions</span>
        </div>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#111827' }}>Bridge Club Promotions</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280' }}>View and manage promotions from all retail partners</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827' }}>{stats.total_promotions}</div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>Total Promotions</div>
          </div>
          <div style={{ background: '#DEF7EC', padding: '16px', borderRadius: '12px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#03543F' }}>{stats.active}</div>
            <div style={{ fontSize: '14px', color: '#03543F' }}>Active Now</div>
          </div>
          <div style={{ background: '#E1EFFE', padding: '16px', borderRadius: '12px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1E40AF' }}>{stats.scheduled}</div>
            <div style={{ fontSize: '14px', color: '#1E40AF' }}>Scheduled</div>
          </div>
          <div style={{ background: '#FEE2E2', padding: '16px', borderRadius: '12px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#991B1B' }}>{stats.inactive}</div>
            <div style={{ fontSize: '14px', color: '#991B1B' }}>Deactivated</div>
          </div>
          <div style={{ background: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827' }}>{stats.stores_with_promotions}</div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>Stores with Promos</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: 'white', padding: '16px', borderRadius: '12px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search promotions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: '1',
              minWidth: '200px',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              minWidth: '180px',
            }}
          >
            <option value="">All Stores</option>
            {stores.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              minWidth: '140px',
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="scheduled">Scheduled</option>
            <option value="expired">Expired</option>
            <option value="inactive">Deactivated</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6B7280' }}>
          Loading promotions...
        </div>
      ) : filteredPromotions.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '48px', margin: '0 0 12px' }}>📢</p>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: '#111827' }}>No Promotions Found</h3>
          <p style={{ margin: 0, color: '#6B7280' }}>
            {searchQuery || selectedStore || selectedStatus
              ? 'Try adjusting your filters'
              : 'No retail partners have created promotions yet'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredPromotions.map((promo) => (
            <div
              key={promo.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                gap: '16px',
                opacity: promo.status === 'inactive' ? 0.6 : 1,
              }}
            >
              {promo.image_url && (
                <img
                  src={promo.image_url}
                  alt={promo.title}
                  style={{
                    width: '120px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    flexShrink: 0,
                  }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <div>
                    <h4 style={{ margin: '0 0 2px', fontSize: '16px', fontWeight: 600, color: '#111827' }}>{promo.title}</h4>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                      {promo.store_name} - {promo.store_city}, {promo.store_state}
                    </p>
                  </div>
                  {getStatusBadge(promo.status)}
                </div>
                <p style={{ margin: '8px 0', fontSize: '14px', color: '#4B5563', lineHeight: 1.4 }}>
                  {promo.message.length > 200 ? promo.message.substring(0, 200) + '...' : promo.message}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                    {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                    {promo.created_by_name && ` | Created by ${promo.created_by_name}`}
                  </div>
                  <button
                    onClick={() => handleToggleActive(promo.id)}
                    style={{
                      background: promo.is_active ? '#FEE2E2' : '#DEF7EC',
                      color: promo.is_active ? '#991B1B' : '#03543F',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    {promo.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BridgeClubPromotions;
